import { ARExperience } from "./arExperience";
import { PortalCanvas } from "./ui/portalCanvas";
import ptBR from "./locales/pt-BR.json";
import en from "./locales/en.json";
import { getDailyModelByPage } from "./dailyModelSelector";
import { api } from "./services/requests";

/* =========================================================
   TYPES
========================================================= */

type TargetConfig = {
  index: number;
  name: string;
  modelUrl: string;
  occluderUrl?: string;
  scale: [number, number, number];
  position: [number, number, number];
  occluderScale?: [number, number, number];
  occluderPosition?: [number, number, number];
};

type Locale = Record<string, any>;
type SupportedLang = "pt-BR" | "en";

/* =========================================================
   I18N STATE
========================================================= */

const locales: Record<string, Locale> = {
  "pt-BR": ptBR,
  "en": en,
};

let currentLocale: Locale = {};
let currentLang: SupportedLang = "pt-BR";

/* =========================================================
   AR STATE
========================================================= */

let arExperienceInstance: ARExperience | null = null;
let isStoppingCamera = false;

/* =========================================================
   HELPERS
========================================================= */

function normalizeLang(lang: string): SupportedLang {
  if (lang === "en") return "en";
  return "pt-BR";
}

function getIntroModelUrl(lang: SupportedLang) {
  return lang === "en"
    ? new URL("./assets/models/intro_en.glb", import.meta.url).href
    : new URL("./assets/models/intro_pt.glb", import.meta.url).href;
}

/* =========================================================
   AR CONFIG
========================================================= */

function getARConfigByPage(page: string, lang: SupportedLang) {
  const sharedOccluder = new URL("./assets/models/occlusion.glb", import.meta.url).href;

  const configs: Record<string, { imageTargetSrc: string; targetConfigs: TargetConfig[] }> = {
    intro: {
      imageTargetSrc: new URL("./assets/targets/targets-intro.mind", import.meta.url).href,
      targetConfigs: [
        {
          index: 0,
          name: "intro-target-1",
          modelUrl: getIntroModelUrl(lang),
          scale: [1.7, 1.7, 1.7],
          position: [0, -0.05, 0],
        },
      ],
    },
    shadowzone: {
      imageTargetSrc: new URL("./assets/targets/targets-shadow.mind", import.meta.url).href,
      targetConfigs: [
        {
          index: 0,
          name: "shadow-target-1",
          modelUrl: getDailyModelByPage("shadowzone"),
          occluderUrl: sharedOccluder,
          scale: [1.7, 1.7, 1.7],
          position: [0, -0.05, 0],
          occluderScale: [1.7, 1.7, 1.7],
          occluderPosition: [0, -0.05, 0],
        },
      ],
    },
    neozone: {
      imageTargetSrc: new URL("./assets/targets/targets-neo.mind", import.meta.url).href,
      targetConfigs: [
        {
          index: 0,
          name: "neo-target-1",
          modelUrl: getDailyModelByPage("neozone"),
          occluderUrl: sharedOccluder,
          scale: [1.7, 1.7, 1.7],
          position: [0, -0.05, 0],
          occluderScale: [1.7, 1.7, 1.7],
          occluderPosition: [0, -0.05, 0],
        },
      ],
    },
    openzone: {
      imageTargetSrc: new URL("./assets/targets/targets-open.mind", import.meta.url).href,
      targetConfigs: [
        {
          index: 0,
          name: "open-target-1",
          modelUrl: getDailyModelByPage("openzone"),
          occluderUrl: sharedOccluder,
          scale: [1.7, 1.7, 1.7],
          position: [0, -0.05, 0],
          occluderScale: [1.7, 1.7, 1.7],
          occluderPosition: [0, -0.05, 0],
        },
      ],
    },
    herozone: {
      imageTargetSrc: new URL("./assets/targets/targets-hero.mind", import.meta.url).href,
      targetConfigs: [
        {
          index: 0,
          name: "hero-target-1",
          modelUrl: getDailyModelByPage("herozone"),
          occluderUrl: sharedOccluder,
          scale: [1.7, 1.7, 1.7],
          position: [0, -0.05, 0],
          occluderScale: [1.7, 1.7, 1.7],
          occluderPosition: [0, -0.05, 0],
        },
      ],
    },
  };

  return configs[page] || null;
}

function getPortalConfigByPage(page: string) {
  const portalConfig: Record<string, { image: string; hue: number }> = {
    intro: {
      image: new URL("./assets/images/portal2.png", import.meta.url).href,
      hue: 280,
    },
    shadowzone: {
      image: new URL("./assets/images/portal1.png", import.meta.url).href,
      hue: 205,
    },
    neozone: {
      image: new URL("./assets/images/portal3.png", import.meta.url).href,
      hue: 28,
    },
    openzone: {
      image: new URL("./assets/images/portal4.png", import.meta.url).href,
      hue: 42,
    },
  };

  return portalConfig[page] || portalConfig["intro"];
}

/* =========================================================
   AR HELPERS
========================================================= */

async function stopCameraIfNeeded(page: string) {
  if (!arExperienceInstance || isStoppingCamera) return;

  isStoppingCamera = true;

  try {
    await arExperienceInstance.stop();
    document.getElementById("container")?.classList.add("hidden");
    console.log("Câmera desligada");
  } catch (error) {
    console.error("Erro ao desligar a câmera:", error);
  } finally {
    isStoppingCamera = false;
  }
}

async function updateIntroModelByLanguage() {
  const page = document.body.dataset.page || "intro";
  if (page !== "intro") return;
  if (!arExperienceInstance) return;

  const introModelUrl = getIntroModelUrl(currentLang);

  await arExperienceInstance.replaceModel("intro-target-1", introModelUrl);
}

function observeScreenChange(page: string) {
  const check = () => {
    const questionActive = document.getElementById("question")?.classList.contains("active");

    const introActive = document.querySelector(".front.intro")?.classList.contains("active");

    if (questionActive || introActive) {
      console.log("Tela sem AR ativa → desligando câmera");
      stopCameraIfNeeded(page);
    }
  };

  const observer = new MutationObserver(check);

  document.body.querySelectorAll(".front").forEach((el) => {
    observer.observe(el, {
      attributes: true,
      attributeFilter: ["class"],
    });
  });

  check();
}

/* =========================================================
   AR INIT
========================================================= */

function initAR(page: string) {
  const header = document.getElementById("header") as HTMLElement | null;

  const arContainer =
    (document.getElementById("stepIntroContainer") as HTMLElement | null) ||
    (document.getElementById("stepNeoZoneContainer") as HTMLElement | null);

  const arConfig = getARConfigByPage(page, currentLang);

  if (!header || !arContainer || !arConfig) return;

  arExperienceInstance = new ARExperience({
    header,
    stepIntroContainer: arContainer,
    imageTargetSrc: arConfig.imageTargetSrc,
    targetConfigs: arConfig.targetConfigs,
    onTargetMatched: async (targetName) => {
      console.log("Target encontrado:", page, targetName);

      const imageTarget = document.getElementById("image-target");
      const firstText = document.getElementById("text-step-1");
      const secondText = document.getElementById("text-step-2");

      imageTarget?.classList.add("hidden");
      firstText?.classList.add("hidden");
      secondText?.classList.remove("hidden");
    },
  });

  arExperienceInstance.init();
}

function initPortal(page: string) {
  const canvas = document.getElementById("portalCanvas") as HTMLCanvasElement | null;
  if (!canvas) return;

  const config = getPortalConfigByPage(page);

  new PortalCanvas(canvas, {
    imageSrc: config.image,
    hue: config.hue,
  }).init();
}

/* =========================================================
   I18N HELPERS
========================================================= */

function getNestedValue(obj: any, path: string): string | undefined {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
}

function updateLanguageButtonsUI() {
  const buttons = document.querySelectorAll<HTMLElement>("[data-lang]");

  buttons.forEach((button) => {
    button.classList.remove("active-lang");
  });

  const activeButton = document.querySelector<HTMLElement>(`[data-lang="${currentLang}"]`);
  activeButton?.classList.add("active-lang");
}

function applyTranslations() {
  document.documentElement.lang = currentLang;

  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const key = el.dataset.i18n;
    if (!key) return;

    const value = getNestedValue(currentLocale, key);
    if (typeof value === "string") {
      el.textContent = value;
    }
  });

  document.querySelectorAll<HTMLElement>("[data-i18n-html]").forEach((el) => {
    const key = el.dataset.i18nHtml;
    if (!key) return;

    const value = getNestedValue(currentLocale, key);
    if (typeof value === "string") {
      el.innerHTML = value;
    }
  });

  document
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-i18n-placeholder]")
    .forEach((el) => {
      const key = el.dataset.i18nPlaceholder;
      if (!key) return;

      const value = getNestedValue(currentLocale, key);
      if (typeof value === "string") {
        el.placeholder = value;
      }
    });

  document.querySelectorAll<HTMLOptionElement>("option[data-i18n]").forEach((el) => {
    const key = el.dataset.i18n;
    if (!key) return;

    const value = getNestedValue(currentLocale, key);
    if (typeof value === "string") {
      el.textContent = value;
    }
  });
}

function loadLocale(lang: string) {
  const safeLang: SupportedLang = lang === "en" ? "en" : "pt-BR";

  currentLang = safeLang;
  currentLocale = locales[safeLang];

  localStorage.setItem("lang", safeLang);

  applyTranslations();
  updateLanguageButtonsUI();
}

/* =========================================================
   LANGUAGE BUTTONS
========================================================= */

function setupLanguageButtons() {
  const buttons = document.querySelectorAll<HTMLButtonElement>("[data-lang]");

  buttons.forEach((button) => {
    button.addEventListener("click", async (event) => {
      event.preventDefault();

      const lang = button.dataset.lang;
      if (!lang) return;

      const nextLang = normalizeLang(lang);

      if (nextLang === currentLang) return;

      loadLocale(nextLang);
      await updateIntroModelByLanguage();
    });
  });
}

/* =========================================================
   APP INIT
========================================================= */

async function initApp() {
  const page = document.body.dataset.page || "intro";
  const savedLang = normalizeLang(localStorage.getItem("lang") || "pt-BR");

  try {
    const response = await api.isActive()
    if (!response.data.active) {
      console.log("A experiência ainda não está ativa. Por favor, tente novamente mais tarde.");
      window.location.href = './inactive.html';
      return;
    }
  }catch(error) {
    console.error("Erro ao verificar o status da experiência:", error);
  };

  setupLanguageButtons();
  loadLocale(savedLang);

  initAR(page);
  initPortal(page);

  observeScreenChange(page);
}

window.addEventListener("DOMContentLoaded", initApp);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register(new URL('./sw.ts', import.meta.url), { type: 'module' })
      .then((registration) => {
        console.log('Service Worker registrado com sucesso:', registration.scope);
      })
      .catch((error) => {
        console.error('Falha ao registrar o Service Worker. O app rodará sem cache offline:', error);
      });
  });
}
